import { useCallback, useMemo, useState } from "react";
import { Button, Chip, Input, type Column } from "@legal-500/legal500-design-system";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal.js";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import {
  testimonialReplayService,
  type ReplayResponse,
} from "../../../api/testimonialReplay.service.js";
import styles from "./SurveyTestimonialReplay.module.css";

/**
 * Re-sends `survey:testimonial:submitted` for the testimonials in the survey
 * responses completed on one UTC day that are still missing from
 * Research → Testimonials. Production Redis was full on
 * 2026-09-30 and those events were lost, so the testimonials never reached
 * Research → Testimonials.
 *
 * The flow mirrors the neighbouring backfill tools: the preview is read-only
 * and lists what would be sent, and sending is a separate, confirmed step
 * that is only offered for the day the preview covered.
 */

type Phase = "idle" | "previewing" | "previewed" | "sending" | "done";

type TableRow = {
  id: string;
  surveyResponseId: string;
  externalId: string;
  arrived: boolean;
};

/** Today as a UTC calendar day, YYYY-MM-DD — the latest day the API accepts. */
function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function testimonials(count: number, qualifier = ""): string {
  return `${count} ${qualifier}testimonial${count === 1 ? "" : "s"}`;
}

function allArrived(count: number): string {
  return count === 1
    ? "The only testimonial already arrived."
    : `All ${count} testimonials already arrived.`;
}

/** The modal's second sentence; nothing to say when none had arrived. */
function arrivedLeftAlone(count: number): string {
  if (count === 0) {
    return "";
  }
  return ` The ${count} that already arrived ${count === 1 ? "is" : "are"} left alone.`;
}

export default function SurveyTestimonialReplay() {
  const { hasPermission } = usePermissions();

  const [maxDate] = useState(todayUtc);
  const [date, setDate] = useState(maxDate);
  const [phase, setPhase] = useState<Phase>("idle");
  const [preview, setPreview] = useState<ReplayResponse | null>(null);
  /** The day the preview covers: sending is only offered for that same day. */
  const [previewDate, setPreviewDate] = useState<string | null>(null);
  const [sent, setSent] = useState<ReplayResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  // A developer tool that re-sends events to the pipeline, gated on
  // developer.write like its Developer-section neighbours.
  const canUse = hasPermission("developer.write");

  const changeDate = useCallback((next: string) => {
    setDate(next);
    // A preview for another day must not stand in for this one.
    setPreview(null);
    setPreviewDate(null);
    setSent(null);
    setError(null);
    setPhase("idle");
  }, []);

  const runPreview = useCallback(async () => {
    setError(null);
    setSent(null);
    setPreview(null);
    setPreviewDate(null);
    setPhase("previewing");
    try {
      const response = await testimonialReplayService.replay(date, true);
      setPreview(response);
      setPreviewDate(date);
      setPhase("previewed");
    } catch (previewError) {
      setError(previewError instanceof Error ? previewError.message : String(previewError));
      setPhase("idle");
    }
  }, [date]);

  const runSend = useCallback(async () => {
    setIsConfirming(false);
    setError(null);
    setPhase("sending");
    try {
      const response = await testimonialReplayService.replay(date, false);
      setSent(response);
      setPhase("done");
    } catch (sendError) {
      // A failed request may still have sent some testimonials — sending the
      // same day again is safe.
      setError(sendError instanceof Error ? sendError.message : String(sendError));
      setPhase("previewed");
    }
  }, [date]);

  const shown = sent ?? preview;

  const tableRows = useMemo<TableRow[]>(
    () =>
      (shown?.testimonials ?? [])
        .map((row, index) => ({
          id: `${index}-${row.surveyResponseId}`,
          surveyResponseId: row.surveyResponseId,
          externalId: row.externalId,
          arrived: row.arrived,
        }))
        // Missing first: those are the rows a send acts on. The sort is
        // stable, so each group keeps the server's order.
        .sort((a, b) => Number(a.arrived) - Number(b.arrived)),
    [shown],
  );

  const columns = useMemo<Column<TableRow>[]>(
    () => [
      {
        header: "Status",
        accessor: "arrived",
        render: (_value, row) =>
          row.arrived ? (
            <Chip size="small" variant="outlined">
              Already arrived
            </Chip>
          ) : (
            <Chip size="small" color="warning">
              Missing
            </Chip>
          ),
      },
      {
        header: "Survey response ID",
        accessor: "surveyResponseId",
        render: (_value, row) => <span className={styles.mono}>{row.surveyResponseId}</span>,
      },
      {
        header: "Testimonial ID",
        accessor: "externalId",
        render: (_value, row) => <span className={styles.mono}>{row.externalId}</span>,
      },
    ],
    [],
  );

  if (!canUse) {
    return (
      <div className={styles.page}>
        <div className={styles.accessDenied} role="alert">
          <h2>Access Denied</h2>
          <p>You do not have permission to view the Survey Testimonial Replay tool.</p>
        </div>
      </div>
    );
  }

  const isBusy = phase === "previewing" || phase === "sending";
  const dateValid = DATE_PATTERN.test(date) && date <= maxDate;
  const found = preview?.testimonialsFound ?? 0;
  const missing = preview?.missing ?? 0;
  const canSend = preview !== null && previewDate === date && missing > 0 && !isBusy;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Survey Testimonial Replay"
        subtitle="Send testimonials from submitted surveys to Research → Testimonials again, one day at a time"
      />

      <p className={styles.intro}>
        Use this when testimonials from submitted surveys are missing from Research → Testimonials,
        for example after an outage. The tool looks at the survey responses completed on the day you
        pick (UTC), takes the testimonial answers from them, and sends those testimonials again.
        Testimonials that already arrived are left alone, and only the missing ones are sent. The
        survey responses themselves are not changed.
      </p>

      {error ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          {error}
        </div>
      ) : null}

      <section className={styles.card} aria-labelledby="replay-step-1">
        <h2 className={styles.cardTitle} id="replay-step-1">
          1 · Pick a day
        </h2>
        <div className={styles.controls}>
          <Input
            type="date"
            label="Survey responses completed on (UTC)"
            value={date}
            max={maxDate}
            onChange={event => changeDate(event.target.value)}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={!dateValid || isBusy}
            onClick={() => void runPreview()}
          >
            Preview
          </Button>
          <Button variant="outlined" disabled={!canSend} onClick={() => setIsConfirming(true)}>
            {preview ? `Send ${testimonials(missing, "missing ")}` : "Send missing testimonials"}
          </Button>
        </div>
        <p className={styles.hint}>
          Preview only reads. Send becomes available after a preview of the same day.
        </p>
      </section>

      {isBusy ? (
        <div className={styles.card} role="status" aria-live="polite">
          <p className={styles.status}>
            {phase === "previewing" ? "Loading preview…" : "Sending testimonials…"}
          </p>
        </div>
      ) : null}

      {preview && !isBusy ? (
        <section className={styles.card} aria-labelledby="replay-step-2">
          <h2 className={styles.cardTitle} id="replay-step-2">
            {`2 · Preview for ${previewDate}`}
          </h2>
          <div className={styles.tiles}>
            <div className={styles.tile}>
              <div className={styles.tileNumber}>{preview.responsesScanned}</div>
              <div className={styles.tileLabel}>Survey responses completed</div>
            </div>
            <div className={styles.tile}>
              <div className={styles.tileNumber}>{preview.testimonialsFound}</div>
              <div className={styles.tileLabel}>Testimonials in those responses</div>
            </div>
            <div
              className={`${styles.tile} ${styles.tileHeadline} ${
                preview.missing > 0 ? styles.tileWarning : styles.tileOk
              }`}
            >
              <div className={styles.tileNumber}>{preview.missing}</div>
              <div className={styles.tileLabel}>Missing from Research → Testimonials</div>
            </div>
          </div>
          {preview.missing === 0 && preview.testimonialsFound > 0 ? (
            <p className={styles.hint}>
              {`Nothing is missing for ${previewDate}. ${allArrived(preview.testimonialsFound)}`}
            </p>
          ) : null}
        </section>
      ) : null}

      {phase === "done" && sent ? (
        <section className={styles.card} aria-labelledby="replay-step-3">
          <h2 className={styles.cardTitle} id="replay-step-3">
            3 · Sent
          </h2>
          <div className={styles.tiles}>
            <div className={`${styles.tile} ${styles.tileOk}`}>
              <div className={styles.tileNumber}>{sent.emitted}</div>
              <div className={styles.tileLabel}>Sent</div>
            </div>
          </div>
          <p className={styles.hint}>
            Sent means handed over, not yet confirmed. Check that the testimonials appear in
            Research → Testimonials, or run Preview again: the missing count should drop to 0. If
            some are still missing, send the same day again.
          </p>
        </section>
      ) : null}

      {shown && !isBusy ? (
        <DataTable
          data={tableRows}
          columns={columns}
          emptyMessage={`No testimonials in survey responses completed on ${previewDate}.`}
        />
      ) : null}

      {isConfirming ? (
        <ConfirmationModal
          title="Send survey testimonials"
          message={
            `${testimonials(missing, "missing ")} from surveys completed on ${date} will be ` +
            `sent to Research → Testimonials.${arrivedLeftAlone(found - missing)}`
          }
          // Nothing here is a warning: sending is safe, and the shared
          // modal renders this slot as a red alert, so it is left empty.
          warningMessage=""
          confirmButtonText={`Send ${testimonials(missing)}`}
          backdropClick={false}
          onClose={() => setIsConfirming(false)}
          onConfirm={runSend}
        />
      ) : null}
    </div>
  );
}
